import { RECIPES } from "../content/recipes";
import { SHIP_FRAMES } from "../content/ships";
import { evaluateBuild } from "../content/skill-paths";
import { WEAPONS, weaponsForSlot } from "../content/weapons";
import type { WeaponId, WeaponSlot } from "../content/schemas";
import { canCraft, isRecipeOwned } from "../simulation/crafting";
import { dailyOpsShortLabel } from "../simulation/daily-ops";
import { extractHeatBonusPct } from "../simulation/extract-heat";
import type { BankedProfile } from "../simulation/persistence";
import { formatNextUnlock, nextCraftUnlock } from "../simulation/run-hooks";
import { currentObjective } from "../simulation/sortie-objective";
import type { WorldSnapshot } from "../simulation/snapshot";
import {
  SORTIE_CONTRACTS,
  canQueueSortieContract,
  type SortieContractId,
} from "../simulation/sortie-contracts";
import {
  CALIBRATION_AXIS_DESCRIPTION,
  CALIBRATION_AXIS_LABEL,
  WEAPON_CALIBRATION_AXES,
  calibrationOffer,
  canCalibrateWeapon,
  type WeaponCalibrationAxis,
} from "../simulation/weapon-calibration";
import type { LeaderboardEntry } from "./leaderboard";
export type CarrierBayHandlers = {
  onCraft: (recipeId: string) => void;
  onFrame: (frameId: string) => void;
  onTrail: (trailId: string) => void;
  onToggleWeapon: (weaponId: WeaponId) => void;
  onCalibrate: (weaponId: WeaponId, axis: WeaponCalibrationAxis) => void;
  onContract: (contractId: SortieContractId) => void;
  onLaunch: () => void;
  onOpenSettings: () => void;
};

const SLOT_ORDER: WeaponSlot[] = ["primary", "secondary", "ordnance"];
const SLOT_LABEL: Record<WeaponSlot, string> = {
  primary: "Primary",
  secondary: "Secondary",
  ordnance: "Ordnance",
};

const TRAILS = [
  { id: "trail_ember", name: "Ember" },
  { id: "trail_ion", name: "Ion" },
  { id: "trail_rust", name: "Rust" },
] as const;

/**
 * Docked hangar console: launch is always on-screen.
 * Craft lives in the Armory drawer. Settings are a separate overlay.
 */
export class CarrierBay {
  private readonly root: HTMLElement;
  private profile: BankedProfile | null = null;
  private lastFramesKey = "";
  private lastWeaponsKey = "";
  private lastTrailsKey = "";
  private lastRecipesKey = "";
  private lastOpsKey = "";
  private lastScoresKey = "";
  private lastBriefKey = "";
  private craftFeedback = "";
  private craftFeedbackAt = 0;
  private armoryOpen = false;
  private scores: readonly LeaderboardEntry[] = [];

  public constructor(
    host: HTMLElement,
    private readonly handlers: CarrierBayHandlers,
  ) {
    this.root = document.createElement("aside");
    this.root.className = "carrier-hangar";
    this.root.innerHTML = `
      <div class="hangar-chrome">
        <div class="hangar-brand">
          <span class="hangar-kicker">MOBILE CARRIER</span>
          <strong>Hangar</strong>
        </div>
        <div class="hangar-meta">
          <span data-rank></span>
          <span data-banked></span>
          <span data-heat hidden></span>
        </div>
        <button type="button" class="hangar-sys" data-open-settings>Systems</button>
      </div>
      <div class="hangar-grid">
        <section class="hangar-brief">
          <div class="hangar-primary" data-primary></div>
          <p class="hangar-last" data-last-run></p>
          <p class="hangar-unlock" data-unlock></p>
          <p class="hangar-build" data-build></p>
          <h3>Daily ops</h3>
          <div data-ops class="hangar-ops"></div>
          <h3>Board</h3>
          <div data-scores class="hangar-scores"></div>
        </section>
        <section class="hangar-rack">
          <div class="hangar-module">
            <span class="hangar-kicker">BAY 01</span>
            <h2>Frame</h2>
            <div data-frames class="hangar-frames"></div>
          </div>
          <div class="hangar-module">
            <span class="hangar-kicker">BAY 02</span>
            <h2>Hardpoints</h2>
            <div data-weapons class="hangar-hardpoints"></div>
          </div>
          <div class="hangar-module">
            <span class="hangar-kicker">BAY 03</span>
            <h2>Trail</h2>
            <div data-trails class="hangar-trails"></div>
          </div>
        </section>
      </div>
      <footer class="hangar-launch-bar">
        <button type="button" class="hangar-secondary" data-armory-toggle>Armory</button>
        <button type="button" class="hangar-launch" data-launch>Launch Expedition</button>
        <p data-pad class="hangar-pad"></p>
        <p data-bay-msg class="hangar-msg"></p>
      </footer>
      <aside class="hangar-armory" data-armory hidden>
        <header>
          <div>
            <span class="hangar-kicker">SPEND SALVAGE</span>
            <strong>Armory</strong>
          </div>
          <button type="button" data-armory-close>Close</button>
        </header>
        <div class="hangar-armory-guidance">
          <p class="hangar-armory-hint">Unlock designs, then tune equipped weapons across permanent calibration tracks.</p>
          <p class="hangar-armory-next" data-armory-next></p>
        </div>
        <div data-recipes class="hangar-recipes"></div>
      </aside>
    `;
    host.appendChild(this.root);
    this.bindStatic();
  }

  public setCraftFeedback(message: string): void {
    this.craftFeedback = message;
    this.craftFeedbackAt = performance.now();
    const msg = this.root.querySelector("[data-bay-msg]");
    if (msg) msg.textContent = message;
  }

  public setScores(entries: readonly LeaderboardEntry[]): void {
    this.scores = entries;
  }

  public sync(profile: BankedProfile, snap: WorldSnapshot): void {
    this.profile = profile;
    const docked = snap.phase === "carrier" || snap.phase === "extracted" || snap.phase === "dead";
    this.root.hidden = !docked;
    this.root.classList.toggle("docked", docked);
    this.root.dataset.armory = this.armoryOpen ? "open" : "shut";
    const armory = this.root.querySelector<HTMLElement>("[data-armory]");
    if (armory) armory.hidden = !this.armoryOpen;

    const rankEl = this.root.querySelector("[data-rank]");
    if (rankEl) {
      rankEl.textContent = `Rank ${profile.pilotMeta.rank} · ${profile.pilotMeta.rankPoints}/${profile.pilotMeta.rankPointsToNext || "—"} RP`;
    }
    const banked = this.root.querySelector("[data-banked]");
    if (banked) banked.textContent = `${profile.salvage} scrap`;
    const heatEl = this.root.querySelector<HTMLElement>("[data-heat]");
    if (heatEl) {
      if (profile.extractStreak > 0) {
        heatEl.hidden = false;
        heatEl.textContent = `Heat ${profile.extractStreak} · +${extractHeatBonusPct(profile.extractStreak)}%`;
      } else {
        heatEl.hidden = true;
      }
    }

    const launch = this.root.querySelector<HTMLButtonElement>("[data-launch]");
    if (launch) {
      launch.disabled = !docked;
      launch.textContent =
        snap.phase === "dead"
          ? "Relaunch"
          : snap.phase === "extracted"
            ? "Launch next"
            : "Launch expedition";
    }

    this.renderBrief(profile, snap);
    this.renderFrames(profile);
    this.renderWeapons(profile);
    this.renderTrails(profile);
    this.renderOps(profile);
    this.renderScores();
    if (this.armoryOpen) this.renderRecipes(profile);
    const armoryNext = this.root.querySelector("[data-armory-next]");
    if (armoryNext)
      armoryNext.textContent = `Next purchase · ${formatNextUnlock(nextCraftUnlock(profile))}`;

    const msg = this.root.querySelector("[data-bay-msg]");
    if (msg) {
      const feedbackFresh = this.craftFeedback && performance.now() - this.craftFeedbackAt < 4000;
      if (feedbackFresh) {
        msg.textContent = this.craftFeedback;
      } else if (snap.phase === "extracting") {
        msg.textContent = `Extracting… ${(snap.extractProgress * 100).toFixed(0)}%`;
      } else {
        msg.textContent = "E or Launch · pad is live";
      }
    }
  }

  public dispose(): void {
    this.root.remove();
  }

  private renderBrief(profile: BankedProfile, snap: WorldSnapshot): void {
    const last = this.root.querySelector("[data-last-run]");
    const unlock = this.root.querySelector("[data-unlock]");
    const primary = this.root.querySelector("[data-primary]");
    const build = this.root.querySelector("[data-build]");
    const pad = this.root.querySelector("[data-pad]");
    const lastRun = profile.lastRun;
    const lastText = lastRun
      ? `Last ${lastRun.outcome === "extracted" ? "extract" : "hull loss"} · Lv ${lastRun.level} · ${lastRun.score.toLocaleString()} pts`
      : "No sorties logged. Launch to start the first run.";
    const unlockText = formatNextUnlock(nextCraftUnlock(profile));
    const obj = currentObjective(snap, profile);
    const verdict = evaluateBuild(snap.runProgress.takenNodes);
    const padText = `${SHIP_FRAMES[profile.selectedFrameId]?.name ?? profile.selectedFrameId} · pad live`;
    const key = `${lastText}|${unlockText}|${obj.title}|${obj.detail}|${obj.tone}|${verdict.label}|${padText}`;
    if (key === this.lastBriefKey) return;
    this.lastBriefKey = key;
    if (last) last.textContent = lastText;
    if (unlock) unlock.textContent = unlockText;
    if (primary) {
      primary.innerHTML = `<em>${obj.title}</em><strong>${obj.detail}</strong>`;
      primary.setAttribute("data-tone", obj.tone);
    }
    if (build) build.textContent = `${verdict.label} — ${verdict.detail}`;
    if (pad) pad.textContent = padText;
  }

  private renderFrames(profile: BankedProfile): void {
    const host = this.root.querySelector("[data-frames]");
    if (!host) return;
    const key = `${profile.selectedFrameId}|${profile.unlockedFrames.join(",")}`;
    if (key === this.lastFramesKey) return;
    this.lastFramesKey = key;
    host.replaceChildren();
    for (const frame of Object.values(SHIP_FRAMES)) {
      const unlocked = profile.unlockedFrames.includes(frame.id);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "hangar-plate";
      btn.dataset.action = "frame";
      btn.dataset.frame = frame.id;
      btn.dataset.selected = String(profile.selectedFrameId === frame.id);
      btn.disabled = !unlocked;
      btn.innerHTML = `<strong>${frame.name}</strong><span>${unlocked ? frame.role : "Locked"}</span>`;
      btn.title = unlocked ? `${frame.role} — ${frame.drawback}` : "Craft this frame in the Armory";
      host.appendChild(btn);
    }
  }

  private renderWeapons(profile: BankedProfile): void {
    const host = this.root.querySelector("[data-weapons]");
    if (!host) return;
    const key = `${profile.selectedWeapons.join(",")}|${profile.unlockedWeapons.join(",")}`;
    if (key === this.lastWeaponsKey) return;
    this.lastWeaponsKey = key;
    host.replaceChildren();
    for (const slot of SLOT_ORDER) {
      const row = document.createElement("div");
      row.className = "hangar-slot";
      const label = document.createElement("span");
      label.className = "hangar-slot-label";
      label.textContent = SLOT_LABEL[slot];
      const chips = document.createElement("div");
      chips.className = "hangar-slot-options";
      for (const weapon of weaponsForSlot(slot)) {
        const unlocked = profile.unlockedWeapons.includes(weapon.id);
        const selected = profile.selectedWeapons.includes(weapon.id);
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "hangar-plate";
        btn.dataset.action = "weapon";
        btn.dataset.weapon = weapon.id;
        btn.dataset.selected = String(selected);
        btn.disabled = !unlocked;
        btn.textContent = unlocked ? weapon.name : "—";
        btn.title = unlocked
          ? `${weapon.tradeoff} — ${weapon.description}`
          : `${weapon.name} locked — open Armory`;
        chips.appendChild(btn);
      }
      row.append(label, chips);
      host.appendChild(row);
    }
  }

  private renderTrails(profile: BankedProfile): void {
    const host = this.root.querySelector("[data-trails]");
    if (!host) return;
    const key = `${profile.selectedTrailId}|${profile.cosmetics.join(",")}`;
    if (key === this.lastTrailsKey) return;
    this.lastTrailsKey = key;
    host.replaceChildren();
    for (const trail of TRAILS) {
      const unlocked = profile.cosmetics.includes(trail.id);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "hangar-plate";
      btn.dataset.action = "trail";
      btn.dataset.trail = trail.id;
      btn.dataset.selected = String(profile.selectedTrailId === trail.id);
      btn.disabled = !unlocked;
      btn.textContent = unlocked ? trail.name : "—";
      host.appendChild(btn);
    }
  }

  private renderOps(profile: BankedProfile): void {
    const host = this.root.querySelector("[data-ops]");
    if (!host) return;
    const ops = profile.dailyOps?.ops ?? [];
    const key = ops.map((op) => `${op.id}:${op.progress}:${op.claimed ? 1 : 0}`).join("|");
    if (key === this.lastOpsKey) return;
    this.lastOpsKey = key;
    if (ops.length === 0) {
      host.textContent = "No contracts today.";
      return;
    }
    host.innerHTML = ops
      .map((op) => {
        const done = op.claimed || op.progress >= op.target;
        const fill = op.target > 0 ? Math.min(100, (op.progress / op.target) * 100) : 0;
        return `<div class="hangar-op${done ? " done" : ""}"><span class="hangar-op-fill" style="width:${fill}%"></span><strong>${dailyOpsShortLabel(op)}</strong><em>+${op.salvageReward}</em></div>`;
      })
      .join("");
  }

  private renderScores(): void {
    const host = this.root.querySelector("[data-scores]");
    if (!host) return;
    const key = this.scores.map((e) => `${e.playerId}:${e.score}`).join("|");
    if (key === this.lastScoresKey) return;
    this.lastScoresKey = key;
    if (this.scores.length === 0) {
      host.textContent = "No posted scores yet.";
      return;
    }
    host.innerHTML = this.scores
      .slice(0, 4)
      .map(
        (entry, i) =>
          `<div class="hangar-score"><span>#${i + 1}</span><strong>${entry.score.toLocaleString()}</strong></div>`,
      )
      .join("");
  }

  private renderRecipes(profile: BankedProfile): void {
    const host = this.root.querySelector("[data-recipes]");
    if (!host) return;
    const key = [
      profile.salvage,
      profile.unlockedFrames.join(","),
      profile.unlockedWeapons.join(","),
      profile.cosmetics.join(","),
      profile.blueprints.join(","),
      JSON.stringify(profile.weaponCalibration),
      profile.queuedSortieContract ?? "",
    ].join("|");
    if (key === this.lastRecipesKey) return;
    this.lastRecipesKey = key;
    host.replaceChildren();

    const contractHeading = document.createElement("h4");
    contractHeading.className = "recipe-group";
    contractHeading.textContent = "Repeatable sortie contracts";
    host.appendChild(contractHeading);
    for (const contract of Object.values(SORTIE_CONTRACTS)) {
      const gate = canQueueSortieContract(profile, contract.id);
      const armed = profile.queuedSortieContract === contract.id;
      const row = document.createElement("div");
      row.className = "recipe-row contract-row";
      row.dataset.contract = contract.id;
      const meta = document.createElement("div");
      meta.innerHTML = `<strong>${contract.name}</strong><em class="recipe-slot">Single sortie</em><span>${contract.description}</span><em>${contract.salvageCost} salvage · Rank ${contract.minRank}</em>`;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.dataset.action = "contract";
      btn.dataset.contract = contract.id;
      btn.disabled = !gate.ok;
      btn.textContent = armed
        ? "Armed"
        : gate.ok
          ? "Arm"
          : contractFailLabel(gate.message, contract.salvageCost, contract.minRank);
      btn.title = gate.ok ? `Spend ${contract.salvageCost} salvage for the next run` : gate.message;
      row.append(meta, btn);
      host.appendChild(row);
    }

    const groups: { title: string; filter: (r: (typeof RECIPES)[number]) => boolean }[] = [
      { title: "Weapons", filter: (r) => r.kind === "weapon" },
      { title: "Frames & kits", filter: (r) => r.kind !== "weapon" },
    ];

    for (const group of groups) {
      const recipes = RECIPES.filter(group.filter);
      if (recipes.length === 0) continue;
      const heading = document.createElement("h4");
      heading.className = "recipe-group";
      heading.textContent = group.title;
      host.appendChild(heading);
      for (const recipe of recipes) {
        const owned = isRecipeOwned(profile, recipe);
        const gate = canCraft(profile, recipe);
        const row = document.createElement("div");
        row.className = "recipe-row";
        if (recipe.kind === "weapon" && recipe.unlocksWeapon) {
          row.dataset.slot = WEAPONS[recipe.unlocksWeapon]?.slot ?? "";
        }
        const meta = document.createElement("div");
        const slotTag =
          recipe.kind === "weapon" && recipe.unlocksWeapon
            ? `<em class="recipe-slot">${WEAPONS[recipe.unlocksWeapon]?.slot ?? ""}</em>`
            : "";
        meta.innerHTML = `<strong>${recipe.name}</strong>${slotTag}<span>${recipe.description}</span><em>${recipe.salvageCost} salvage${recipe.requiresRank ? ` · Rank ${recipe.requiresRank}` : ""}${recipe.requiresBlueprint ? ` · needs ${recipe.requiresBlueprint}` : ""}</em>`;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.dataset.action = "craft";
        btn.dataset.recipe = recipe.id;
        if (owned) {
          btn.textContent = "Owned";
          btn.disabled = true;
        } else if (!gate.ok) {
          btn.textContent = craftFailLabel(gate.message, recipe.salvageCost);
          btn.disabled = true;
          btn.title = gate.message;
        } else {
          btn.textContent = "Craft";
          btn.title = `Spend ${recipe.salvageCost} salvage`;
        }
        row.append(meta, btn);
        host.appendChild(row);
      }
    }

    const calibrationHeading = document.createElement("h4");
    calibrationHeading.className = "recipe-group";
    calibrationHeading.textContent = "Equipped weapon calibration";
    host.appendChild(calibrationHeading);
    for (const weaponId of profile.selectedWeapons) {
      const weapon = WEAPONS[weaponId];
      if (!weapon) continue;
      for (const axis of WEAPON_CALIBRATION_AXES) {
        const offer = calibrationOffer(profile, weaponId, axis);
        const gate = canCalibrateWeapon(profile, weaponId, axis);
        const row = document.createElement("div");
        row.className = "recipe-row calibration-row";
        row.dataset.slot = weapon.slot;
        const meta = document.createElement("div");
        meta.innerHTML = `<strong>${weapon.name} · ${CALIBRATION_AXIS_LABEL[axis]}</strong><em class="recipe-slot">Lv ${offer.currentLevel}</em><span>${CALIBRATION_AXIS_DESCRIPTION[axis]}</span><em>${offer.maxed ? "Track complete" : `${offer.cost} salvage · Rank ${offer.requiredRank}`}</em>`;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.dataset.action = "calibrate";
        btn.dataset.weapon = weaponId;
        btn.dataset.axis = axis;
        btn.disabled = !gate.ok;
        btn.textContent = offer.maxed
          ? "Max"
          : gate.ok
            ? `Tune ${offer.nextLevel}`
            : calibrationFailLabel(gate.message, offer.cost, offer.requiredRank);
        btn.title = gate.ok ? `Spend ${offer.cost} salvage` : gate.message;
        row.append(meta, btn);
        host.appendChild(row);
      }
    }
  }

  private setArmory(open: boolean): void {
    this.armoryOpen = open;
    this.lastRecipesKey = "";
    const armory = this.root.querySelector<HTMLElement>("[data-armory]");
    if (armory) armory.hidden = !open;
    this.root.dataset.armory = open ? "open" : "shut";
    if (open && this.profile) this.renderRecipes(this.profile);
  }

  private bindStatic(): void {
    this.root.querySelector("[data-launch]")?.addEventListener("click", () => {
      this.handlers.onLaunch();
    });
    this.root.querySelector("[data-open-settings]")?.addEventListener("click", () => {
      this.handlers.onOpenSettings();
    });
    this.root.querySelector("[data-armory-toggle]")?.addEventListener("click", () => {
      this.setArmory(!this.armoryOpen);
    });
    this.root.querySelector("[data-armory-close]")?.addEventListener("click", () => {
      this.setArmory(false);
    });

    this.root.addEventListener("click", (event) => {
      const target = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        "button[data-action]",
      );
      if (!target || target.disabled) return;
      const action = target.dataset.action;
      if (action === "craft" && target.dataset.recipe) {
        this.handlers.onCraft(target.dataset.recipe);
      } else if (action === "frame" && target.dataset.frame) {
        this.handlers.onFrame(target.dataset.frame);
      } else if (action === "trail" && target.dataset.trail) {
        this.handlers.onTrail(target.dataset.trail);
      } else if (action === "weapon" && target.dataset.weapon) {
        this.handlers.onToggleWeapon(target.dataset.weapon as WeaponId);
      } else if (
        action === "calibrate" &&
        target.dataset.weapon &&
        target.dataset.axis &&
        WEAPON_CALIBRATION_AXES.includes(target.dataset.axis as WeaponCalibrationAxis)
      ) {
        this.handlers.onCalibrate(
          target.dataset.weapon as WeaponId,
          target.dataset.axis as WeaponCalibrationAxis,
        );
      } else if (action === "contract" && target.dataset.contract) {
        this.handlers.onContract(target.dataset.contract as SortieContractId);
      }
    });
  }
}

function craftFailLabel(message: string, cost: number): string {
  if (message.startsWith("Need ") && message.includes("salvage")) return `Need ${cost}`;
  if (message.startsWith("Requires Pilot Rank")) return message.replace("Requires Pilot ", "");
  if (message.startsWith("Requires blueprint")) return "Need BP";
  if (message === "Already owned.") return "Owned";
  return "Locked";
}

function calibrationFailLabel(message: string, cost: number, rank: number): string {
  if (message.startsWith("Need ")) return `Need ${cost}`;
  if (message.startsWith("Requires Pilot Rank")) return `Rank ${rank}`;
  if (message.includes("maxed")) return "Max";
  return "Locked";
}

function contractFailLabel(message: string, cost: number, rank: number): string {
  if (message.includes("already armed")) return "Armed";
  if (message.startsWith("Need ")) return `Need ${cost}`;
  if (message.startsWith("Requires Pilot Rank")) return `Rank ${rank}`;
  return "Locked";
}
